<template>
  <div class="office-catalog">
    <v-container grid-list-md>
      <v-layout row wrap align-center class="mb-3">
        <v-flex xs12 md8>
          <h1 class="display-1 font-weight-bold mb-1">Catálogo de oficinas</h1>
          <p class="subheading grey--text">Registra y controla las oficinas antes de vincularlas con una empresa.</p>
        </v-flex>
        <v-flex xs12 md4 class="text-md-right">
          <v-btn color="primary" large @click="openCreate"><v-icon left>add</v-icon>Nueva oficina</v-btn>
        </v-flex>
      </v-layout>

      <v-alert v-if="error" type="error" dismissible :value="true" @input="error = ''">{{ error }}</v-alert>

      <v-card class="elevation-2">
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 md8>
              <v-text-field v-model="search" prepend-icon="search" label="Buscar oficina" clearable @keyup.enter="fetchOffices"></v-text-field>
            </v-flex>
            <v-flex xs12 md4>
              <v-select v-model="statusFilter" :items="statusOptions" item-text="text" item-value="value" label="Estatus" clearable @change="fetchOffices"></v-select>
            </v-flex>
          </v-layout>

          <v-data-table :headers="headers" :items="offices" :loading="loading" :rows-per-page-items="[10, 25, 50]" :rows-per-page="25" no-data-text="No hay oficinas registradas">
            <template v-slot:items="props">
              <td>{{ props.item.code }}</td>
              <td class="text-xs-center">
                <v-chip small :color="props.item.status === 'AVAILABLE' ? 'success' : 'blue-grey'" text-color="white">
                  {{ props.item.status === 'AVAILABLE' ? 'Disponible' : 'Vinculada' }}
                </v-chip>
              </td>
              <td>{{ props.item.preloaded_company_name || '-' }}</td>
              <td class="text-xs-center">
                <v-icon small class="mr-3" title="Editar" @click="openEdit(props.item)">edit</v-icon>
                <v-icon small :class="{ 'grey--text': props.item.preloaded_company }" title="Eliminar" @click="confirmDelete(props.item)">delete</v-icon>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <v-dialog v-model="formDialog" max-width="480px" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ editingOffice ? 'Editar oficina' : 'Nueva oficina' }}</v-toolbar-title>
          <v-spacer></v-spacer><v-btn icon @click="formDialog = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text>
          <v-form ref="officeForm" v-model="formValid">
            <v-text-field v-model="form.code" label="Clave o número de oficina *" prepend-icon="meeting_room" placeholder="Ej. 3-A" :rules="requiredRules" :error-messages="formErrors.code"></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer><v-btn flat @click="formDialog = false">Cancelar</v-btn><v-btn color="primary" :loading="saving" @click="saveOffice">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="460px">
      <v-card>
        <v-card-title class="headline">Eliminar oficina</v-card-title>
        <v-card-text>¿Deseas eliminar la oficina {{ deletingOffice ? deletingOffice.code : '' }}?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer><v-btn flat @click="deleteDialog = false">Cancelar</v-btn><v-btn color="error" :loading="deleting" @click="deleteOffice">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = process.env.VUE_APP_API_URL || 'https://empresa-service.intrare.app'

export default {
  name: 'CorporateBuildingOfficeCatalog',
  data() {
    return {
      offices: [], loading: false, saving: false, deleting: false,
      search: '', statusFilter: null, error: '', formDialog: false, deleteDialog: false,
      editingOffice: null, deletingOffice: null, form: { code: '' }, formErrors: {}, formValid: false,
      requiredRules: [value => !!value || 'Este campo es requerido.'],
      statusOptions: [{ text: 'Disponible', value: 'AVAILABLE' }, { text: 'Vinculada', value: 'OCCUPIED' }],
      headers: [
        { text: 'Oficina', value: 'code' },
        { text: 'Estatus', value: 'status', align: 'center' },
        { text: 'Empresa vinculada', value: 'preloaded_company_name' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ]
    }
  },
  mounted() { this.fetchOffices() },
  methods: {
    authHeaders() { return { 'Content-Type': 'application/json', Authorization: 'Token ' + store.getters.getToken } },
    notify(message) {
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_activate_true')
    },
    fetchOffices() {
      this.loading = true
      const params = {}
      if (this.search) params.search = this.search
      if (this.statusFilter) params.status = this.statusFilter
      axios.get(`${API_BASE_URL}/Parking/building-offices/`, { headers: this.authHeaders(), params: params })
        .then(response => { this.offices = Array.isArray(response.data) ? response.data : (response.data.results || []) })
        .catch(() => { this.error = 'No fue posible cargar el catálogo de oficinas.' })
        .finally(() => { this.loading = false })
    },
    openCreate() {
      this.editingOffice = null
      this.form = { code: '' }
      this.formErrors = {}
      this.formDialog = true
      this.$nextTick(() => { if (this.$refs.officeForm) this.$refs.officeForm.resetValidation() })
    },
    openEdit(office) {
      this.editingOffice = office
      this.form = { code: office.code }
      this.formErrors = {}
      this.formDialog = true
    },
    saveOffice() {
      if (!this.$refs.officeForm.validate()) return
      this.saving = true
      this.formErrors = {}
      const request = this.editingOffice
        ? axios.patch(`${API_BASE_URL}/Parking/building-offices/${this.editingOffice.id}/`, this.form, { headers: this.authHeaders() })
        : axios.post(`${API_BASE_URL}/Parking/building-offices/`, this.form, { headers: this.authHeaders() })
      request.then(() => {
        this.formDialog = false
        this.notify(this.editingOffice ? 'Oficina actualizada.' : 'Oficina registrada.')
        this.fetchOffices()
      }).catch(error => {
        const data = error.response && error.response.data
        if (data && typeof data === 'object') this.formErrors = data
        else this.error = 'No fue posible guardar la oficina.'
      }).finally(() => { this.saving = false })
    },
    confirmDelete(office) {
      if (office.preloaded_company) {
        this.error = 'No se puede eliminar una oficina vinculada con una empresa.'
        return
      }
      this.deletingOffice = office
      this.deleteDialog = true
    },
    deleteOffice() {
      this.deleting = true
      axios.delete(`${API_BASE_URL}/Parking/building-offices/${this.deletingOffice.id}/`, { headers: this.authHeaders() })
        .then(() => { this.deleteDialog = false; this.notify('Oficina eliminada.'); this.fetchOffices() })
        .catch(error => {
          const data = error.response && error.response.data
          this.error = (data && data.detail) || 'No fue posible eliminar la oficina.'
          this.deleteDialog = false
        })
        .finally(() => { this.deleting = false })
    }
  }
}
</script>

<style scoped>
.office-catalog { padding: 20px; }
</style>
